import React, { useEffect, useState } from 'react';
import UploadTab from './UploadTab';
import VersionsTab from './VersionsTab';
import {
  getLatestVersion, hydrateEstimateVersions, isHydrated,
  getHydrateError, retryHydrate, onHydrateChange,
} from './storage';

type InnerTab = 'upload' | 'versions';

const EstimateUploads: React.FC = () => {
  const [tab, setTab] = useState<InnerTab>('upload');
  const [refreshKey, setRefreshKey] = useState(0);

  // Hydrate estimate versions from Snowflake on first visit to this tab.
  // Cache is shared with MediaPlanBuilder, so navigating between them
  // re-uses the same data with no second spinner.
  const [estimatesHydrated, setEstimatesHydrated] = useState<boolean>(isHydrated());
  // Mirror hydrate error into local state so banner re-renders on retry.
  const [hydrateErr, setHydrateErr] = useState<string | null>(getHydrateError());
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    if (estimatesHydrated) return;
    hydrateEstimateVersions().finally(() => {
      setEstimatesHydrated(true);
      setHydrateErr(getHydrateError());
    });
  }, [estimatesHydrated]);
  useEffect(() => {
    return onHydrateChange(() => setHydrateErr(getHydrateError()));
  }, []);

  async function handleRetry() {
    setRetrying(true);
    await retryHydrate();
    setHydrateErr(getHydrateError());
    setRefreshKey(k => k + 1);
    setRetrying(false);
  }

  if (!estimatesHydrated) {
    return (
      <div style={{
        minHeight: 'calc(100vh - 64px)',
        background: '#f5f7fa',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 14,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          border: '3px solid #cbd5e1', borderTopColor: '#2563eb',
          animation: 'estim-spin 0.8s linear infinite',
        }} />
        <div style={{ fontSize: 13, color: '#64748b' }}>Loading estimates…</div>
        <style>{`@keyframes estim-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: 'calc(100vh - 64px)',
      background: '#f5f7fa',
      padding: '32px 32px 48px',
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        {/* Page header with icon */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 16,
          marginBottom: 24,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 10,
            background: '#eff6ff', color: '#2563eb',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              fontSize: 24, fontWeight: 700, color: '#0f172a',
              margin: '0 0 4px', letterSpacing: '-0.01em',
            }}>
              Estimate Uploads
            </h1>
            <p style={{
              fontSize: 13.5, color: '#64748b', margin: 0, lineHeight: 1.5,
            }}>
              Upload new MVMS Syndication Upfront Estimates and manage estimate versions.
            </p>
          </div>
        </div>

        {/* Hydrate-failure banner — shown above the card when /estimate-versions
            could not be loaded. Versions tab will be empty and Upload tab's
            pre-checks (duplicate as-of / older-than-latest) won't function
            until the cache is hydrated. */}
        {hydrateErr && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 12,
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="#dc2626" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: '#991b1b' }}>
                Couldn&apos;t load estimate versions
              </div>
              <div style={{ fontSize: 12.5, color: '#7f1d1d', marginTop: 2 }}>
                {hydrateErr}
              </div>
            </div>
            <button onClick={handleRetry} disabled={retrying}
              style={{
                padding: '6px 14px',
                background: retrying ? '#fca5a5' : '#dc2626',
                color: '#fff', border: 'none', borderRadius: 6,
                fontSize: 12.5, fontWeight: 600,
                cursor: retrying ? 'not-allowed' : 'pointer',
              }}>
              {retrying ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        {/* Card with tabs + panel */}
        <div style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
          overflow: 'hidden',
          marginBottom: 16,
        }}>
          {/* Tab strip */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            paddingLeft: 12,
          }}>
            <TabButton active={tab === 'upload'} onClick={() => setTab('upload')}>
              Upload
            </TabButton>
            <TabButton active={tab === 'versions'} onClick={() => setTab('versions')}>
              Versions
            </TabButton>
          </div>

          {/* Panel */}
          {tab === 'upload' && (
            <UploadTab onUploaded={() => {
              setRefreshKey(k => k + 1);
              setTab('versions');
            }} />
          )}
          {tab === 'versions' && <VersionsTab refreshKey={refreshKey} />}
        </div>

        {/* Bottom help card — only shows on Upload tab. Pass refreshKey so
            the disabled state re-evaluates after a successful upload. */}
        {tab === 'upload' && <HelpCard refreshKey={refreshKey} />}
      </div>
    </div>
  );
};

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: '14px 20px',
      background: 'transparent',
      border: 'none',
      borderBottom: `2px solid ${active ? '#2563eb' : 'transparent'}`,
      color: active ? '#2563eb' : '#64748b',
      fontSize: 13.5,
      fontWeight: active ? 700 : 500,
      cursor: 'pointer',
      marginBottom: -1,
      transition: 'color 0.15s, border-color 0.15s',
    }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.color = '#1e293b'; }}
    onMouseLeave={e => { if (!active) e.currentTarget.style.color = '#64748b'; }}>
    {children}
  </button>
);

/**
 * Help card at the bottom of the Upload tab. Renders a "Download Template"
 * action that downloads the latest uploaded version's xlsx — disabled when
 * no version exists yet. (TODO: actual download wiring once we decide
 * between re-export-from-parsed and store-original-blob.)
 */
const HelpCard: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  // refreshKey participates so the disabled state re-evaluates after upload.
  const hasLatest = React.useMemo(() => getLatestVersion() != null, [refreshKey]);
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 10,
      padding: '16px 20px',
      display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: '#eff6ff', color: '#2563eb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>
          Need help with the file format?
        </div>
        <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
          Download the latest template to see the expected layout.
        </div>
      </div>
      <button
        disabled={!hasLatest}
        title={hasLatest
          ? 'Download the latest uploaded version as a reference template'
          : 'No template available — upload an estimate version first'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '8px 14px',
          background: '#fff',
          border: '1px solid #cbd5e1',
          borderRadius: 6,
          fontSize: 13, fontWeight: 600,
          color: hasLatest ? '#0f172a' : '#94a3b8',
          cursor: hasLatest ? 'pointer' : 'not-allowed',
          opacity: hasLatest ? 1 : 0.6,
          transition: 'background 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { if (hasLatest) e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { if (hasLatest) e.currentTarget.style.background = '#fff'; }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Download Template
      </button>
    </div>
  );
};

export default EstimateUploads;
